import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const provider = params.get("provider");
  const paypalToken = params.get("token");
  const [status, setStatus] = useState("loading");
  const { clearCart } = useCart();
  const cleared = useRef(false);

  const markPaid = () => {
    setStatus("paid");
    if (!cleared.current) {
      cleared.current = true;
      clearCart();
    }
  };

  useEffect(() => {
    if (provider === "paypal") {
      if (!paypalToken) {
        setStatus("error");
        return;
      }
      api
        .post("/paypal/capture", { order_id: paypalToken })
        .then((res) => {
          if (res.data.payment_status === "paid") {
            markPaid();
          } else {
            setStatus("pending");
          }
        })
        .catch(() => setStatus("error"));
      return;
    }
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          markPaid();
          return;
        }
      } catch {
        /* keep polling */
      }
      attempts += 1;
      if (attempts < 10) {
        timer = setTimeout(poll, 2000);
      } else {
        setStatus("pending");
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, provider, paypalToken]);

  return (
    <div
      className="mx-auto flex min-h-screen max-w-[1700px] flex-col items-start justify-center px-5 pt-20 sm:px-8 lg:px-16"
      data-testid="payment-success-page"
    >
      {status === "loading" && (
        <div className="flex items-center gap-4" data-testid="payment-status-loading">
          <Loader2 className="h-6 w-6 animate-spin text-ember" />
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-400">
            Confirmation du paiement…
          </p>
        </div>
      )}

      {status === "paid" && (
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <CheckCircle2 className="h-12 w-12 text-ember" />
          <h1
            className="mt-8 font-blackletter text-6xl leading-[1] sm:text-8xl"
            data-testid="payment-confirmed-heading"
          >
            Merci.
          </h1>
          <p className="mt-6 max-w-md font-serif text-xl italic leading-relaxed text-zinc-400">
            Votre commande est confirmée. Chaque pièce part de l’atelier sous 48h — emballée, numérotée,
            signée.
          </p>
          <Link
            to="/collections"
            data-testid="payment-success-continue-link"
            className="mt-10 inline-flex h-14 items-center border border-white/15 px-8 font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-200 transition-colors duration-300 hover:border-ember hover:text-ember"
          >
            Retour à la collection
          </Link>
        </motion.div>
      )}

      {status === "pending" && (
        <div data-testid="payment-status-pending">
          <h1 className="font-blackletter text-5xl leading-[1] sm:text-7xl">
            Presque.
          </h1>
          <p className="mt-6 max-w-md font-serif text-xl italic text-zinc-400">
            Le paiement est en cours de traitement. Vous recevrez une confirmation par email.
          </p>
        </div>
      )}

      {status === "error" && (
        <div data-testid="payment-status-error">
          <h1 className="font-blackletter text-5xl leading-[1] sm:text-7xl">
            Session introuvable.
          </h1>
          <Link
            to="/collections"
            data-testid="payment-error-back-link"
            className="mt-10 inline-flex h-14 items-center border border-white/15 px-8 font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-200 hover:border-ember hover:text-ember"
          >
            Retour à la collection
          </Link>
        </div>
      )}
    </div>
  );
}
